import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { navigateToSection } from "../utils/hashSectionNav";
import { LanguageSwitcher } from "./LanguageSwitcher";

const MENU_ID = "primary-navigation";
const DESKTOP_BREAKPOINT = 768;
/* Histerese: evita ligar/desligar a classe na mesma limiar a cada frame. */
const SCROLLED_ON = 48;
const SCROLLED_OFF = 12;

export const Navigation = (props) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [aboutInView, setAboutInView] = useState(false);

  const isLanding = location.pathname === "/";
  const closeMenu = useCallback(() => setIsOpen(false), []);

  /* Topbar sólida assim que sai do topo. Sobre o hero da home, fica translúcida. */
  useEffect(() => {
    let ticking = false;

    const updateScrolled = () => {
      const y = window.scrollY;
      setIsScrolled((prev) => {
        const next = prev ? y > SCROLLED_OFF : y > SCROLLED_ON;
        return next === prev ? prev : next;
      });
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateScrolled);
    };

    updateScrolled();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Fecha o menu mobile ao voltar para desktop e ao apertar Esc. */
  useEffect(() => {
    if (!isOpen) return;

    const onResize = () => {
      if (window.innerWidth >= DESKTOP_BREAKPOINT) closeMenu();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeMenu();
    };

    window.addEventListener("resize", onResize);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("resize", onResize);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, closeMenu]);

  /* Marca "Sobre nós" como ativo enquanto a seção estiver na tela. */
  useEffect(() => {
    if (!isLanding || !("IntersectionObserver" in window)) {
      setAboutInView(false);
      return;
    }
    const section = document.getElementById("about");
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setAboutInView((prev) =>
          entry.isIntersecting === prev ? prev : entry.isIntersecting
        );
      },
      /* rootMargin negativo no topo compensa a topbar fixa e reduz oscilação. */
      { threshold: 0.2, rootMargin: "-72px 0px -35% 0px" }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, [isLanding]);

  const goToSection = (event, sectionId) => {
    event.preventDefault();
    closeMenu();
    navigateToSection(navigate, location, sectionId);
  };

  const openInNewTab = (url) => {
    closeMenu();
    window.open(url, "_blank", "noreferrer");
  };

  const domain = props.data ? props.data.nameBankDomain : null;
  const urlRegister = domain ? `https://app.${domain}.com.br/register` : "#";
  const urlLogin = domain ? `https://app.${domain}.com.br/login` : "#";

  const navClass = [
    "navbar navbar-default navbar-fixed-top",
    isScrolled || !isLanding || isOpen ? "is-solid" : "is-transparent",
  ].join(" ");

  return (
    <nav id="menu" className={navClass}>
      <div className="container">
        <div className="navbar-header">
          <button
            type="button"
            className={`navbar-toggle${isOpen ? "" : " collapsed"}`}
            onClick={() => setIsOpen((value) => !value)}
            aria-expanded={isOpen}
            aria-controls={MENU_ID}
          >
            <span className="sr-only">{t("nav.toggleNav")}</span>
            <span className="icon-bar"></span>
            <span className="icon-bar"></span>
            <span className="icon-bar"></span>
          </button>
          <a className="navbar-brand page-scroll" href="/" onClick={closeMenu}>
            <img
              src="/img/logo_ciryon.png?v=20260710-2"
              alt={props.data ? props.data.nameBank : "CIRYON"}
              className="styled-logo"
            />
          </a>
        </div>

        <div
          id={MENU_ID}
          className={`collapse navbar-collapse${isOpen ? " in" : ""}`}
        >
          <ul className="nav navbar-nav navbar-right ul-limited">
            <li className={aboutInView ? "is-active" : undefined}>
              <a
                href="/#about"
                className="page-scroll"
                onClick={(event) => goToSection(event, "about")}
                aria-current={aboutInView ? "true" : undefined}
              >
                {t("nav.about")}
              </a>
            </li>
            <li
              className={
                location.pathname === "/forujob" ? "is-active" : undefined
              }
            >
              <Link
                to="/forujob"
                onClick={closeMenu}
                aria-current={
                  location.pathname === "/forujob" ? "page" : undefined
                }
              >
                {t("nav.forBusiness")}
              </Link>
            </li>
            <li
              className={location.pathname === "/foru" ? "is-active" : undefined}
            >
              <Link
                to="/foru"
                onClick={closeMenu}
                aria-current={location.pathname === "/foru" ? "page" : undefined}
              >
                {t("nav.forYou")}
              </Link>
            </li>
            <li className="nav-cta-wrap">
              <div className="nav-cta">
                <button
                  type="button"
                  className="btn nav-cta-btn nav-cta-btn--ghost"
                  onClick={() => openInNewTab(urlLogin)}
                >
                  {t("nav.accessAccount")}
                </button>
                <button
                  type="button"
                  className="btn nav-cta-btn nav-cta-btn--primary"
                  onClick={() => openInNewTab(urlRegister)}
                >
                  {t("nav.openAccount")}
                </button>
              </div>
            </li>
            <LanguageSwitcher />
          </ul>
        </div>
      </div>
    </nav>
  );
};
